import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgentsApi } from '../apis/agents-apis';
import { Agent } from '../types';
import { useToast } from "../components/ui/Toast";
import AgentForm, { AgentFormRef } from './AgentForm';
import { NavigationWarningDialog } from '../components/NavigationWarningDialog';
import { Spinner } from "../components/ui/Spinner";
import { useNavigationWarning } from '../hooks/useNavigationWarning';

export const AddAgentPage: React.FC = () => {
  const formRef = useRef<AgentFormRef>(null);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [existingAgents, setExistingAgents] = useState<Agent[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  
  const initialAgent: Agent = {
    agentId: '',
    agentName: '',
    agentDescription: '',
    agentPrompt: '',
    agentSelectedSkills: [],
    agentTags: [],
    tags: [],
    isCore: false,
    accessControl: {
      viewAccessType: 'Owner Only',
      viewAllowedUsers: [],
      editAccessType: 'Owner Only',
      editAllowedUsers: []
    },
    ownerId: ''
  };

  const [newAgent, setNewAgent] = useState<Agent>(initialAgent);
  const agentsApi = useAgentsApi();

  // Check if there are any unsaved changes
  const hasUnsavedChanges = !isSaving && JSON.stringify(newAgent) !== JSON.stringify(initialAgent);

  const {
    showDialog: showNavigationWarning,
    handleNavigationAttempt,
    handleConfirmNavigation,
    handleCancelNavigation
  } = useNavigationWarning({
    shouldWarn: hasUnsavedChanges,
    onConfirm: () => {
      setNewAgent(initialAgent);
    },
    message: 'You have unsaved changes that will be lost if you leave this page.'
  });

  useEffect(() => {
    const fetchExistingAgents = async () => {
      try {
        const getAllAgents = true;
        const agents = await agentsApi.getAgents(getAllAgents);
        setExistingAgents(agents);
      } catch (error) {
        console.error('Error fetching agents:', error);
        addToast({
          title: "Error",
          description: "Failed to fetch existing agents",
          variant: "destructive",
          duration: 4000
        });
      } finally {
        setLoading(false);
      }
    };

    fetchExistingAgents();
  }, []);

  const handleSubmit = async () => {
    const isValid = await formRef.current?.validateForm() ?? false;
  
    if (!isValid) {
      addToast({
        title: "Validation Error",
        description: "Please fill in all required fields correctly",
        variant: "destructive",
        duration: 4000
      });
      return;
    }
  
    try {
      setIsSaving(true);
      await agentsApi.createAgent(newAgent);
      addToast({
        title: "Success",
        description: "Agent " + newAgent.agentName + " has been successfully created",
        variant: "default",
        duration: 4000
      });
      navigate('/marketplace/agents');
    } catch (error) {
      console.error('Error creating agent:', error);
      addToast({
        title: "Error",
        description: "Failed to create agent: " + String(error),
        variant: "destructive",
        duration: 8000
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center my-8 gap-2">
        <Spinner className="border-blue-500" />
        <p className="text-center text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <>
      <AgentForm 
        ref={formRef}
        agent={newAgent}
        onAgentChange={setNewAgent}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/marketplace/agents')}
        isEditing={false}
        setIsReadOnly={() => {}}
        existingAgents={existingAgents}
        hasUnsavedChanges={hasUnsavedChanges}
        isSaving={isSaving}
      />

      <NavigationWarningDialog
        open={showNavigationWarning}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        title="Discard Changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
      />
    </>
  );
};

export default AddAgentPage;